// Canonical Malta election survey dataset, copied verbatim from the
// inspection HTML at /malta_polls_inspection.html (the source-of-truth
// spec). Every poll, methodology block, and observation note in this file
// is verbatim from that document. Do not paraphrase when updating.
//
// Date span: 8 June 2024 (EP election anchor) → 16 April 2026 (final
// pre-snap Esprimi poll).

import type { Milestone, Poll, Pollster, PollsterName } from './surveys.types';

// ---- POLLS ----------------------------------------------------------
// 17 surveys + 1 anchor (the 2024 EP election actual result), oldest first.
// `lead` is pre-computed pl − pn for callers' convenience.

export const POLLS: Poll[] = [
  {
    date: '2024-06-08',
    pollster: '2024 EP Election (actual result)',
    sample: null,
    pl: 45.3,
    pn: 42.0,
    adpd: 1.2,
    mom: null,
    other: 11.2,
    raw_pl: null,
    raw_pn: null,
    turnout: 73.0,
    abela: null,
    opp: null,
    opp_name: 'Grech',
    none: null,
    gov_perf: null,
    field: '8 June 2024',
    url: 'https://en.wikipedia.org/wiki/2024_European_Parliament_election_in_Malta',
    notes: 'BASELINE: Actual MEP result. PL won by 8,454 first-count votes.',
    lead: 3.3,
  },
  {
    date: '2024-09-29',
    pollster: 'MaltaToday',
    sample: 712,
    pl: 43.8,
    pn: 48.2,
    adpd: 4.5,
    mom: null,
    other: 3.5,
    raw_pl: null,
    raw_pn: null,
    turnout: 75.7,
    abela: 42.6,
    opp: 26.6,
    opp_name: 'Grech',
    none: 30.7,
    gov_perf: 2.6,
    field: '11–19 Sep 2024',
    url: 'https://www.maltatoday.com.mt/news/data_and_surveys/131444/pn_leads_labour_by_12000_votes_despite_bernard_grechs_poor_showing',
    notes: 'PN lead = 11,699 votes — only PN survey lead in entire post-MEP window.',
    lead: -4.4,
  },
  {
    date: '2024-10-12',
    pollster: 'Sagalytics',
    sample: 1200,
    pl: 48.3,
    pn: 44.1,
    adpd: null,
    mom: null,
    other: 7.6,
    raw_pl: null,
    raw_pn: null,
    turnout: null,
    abela: 36.0,
    opp: 24.0,
    opp_name: 'Grech',
    none: null,
    gov_perf: null,
    field: 'early Oct 2024',
    url: 'https://vincentmarmara.com/wp-content/uploads/2024/10/Is-Sondagg-L-Ewwel-Edizzjoni-Ottubru-2024.pdf',
    notes: "Marmarà's 'Is-Sondaġġ Ed.1' (PDF). PL lead ~12,000 votes.",
    lead: 4.2,
  },
  {
    date: '2024-11-16',
    pollster: 'MaltaToday',
    sample: 707,
    pl: 45.3,
    pn: 46.3,
    adpd: 4.1,
    mom: null,
    other: 4.2,
    raw_pl: null,
    raw_pn: null,
    turnout: 70.6,
    abela: 43.2,
    opp: 25.0,
    opp_name: 'Grech',
    none: 31.8,
    gov_perf: 2.7,
    field: '5–13 Nov 2024',
    url: 'https://www.maltatoday.com.mt/news/data_and_surveys/132309/parties_running_neck_and_neck_as_budget_bounce_not_enough_for_labour_to_overtake_pn',
    notes: 'Post-2025 budget poll. PN lead = 2,647 votes (1pp). Within MoE.',
    lead: -1.0,
  },
  {
    date: '2025-02-08',
    pollster: 'Sagalytics',
    sample: 1200,
    pl: 50.1,
    pn: 44.2,
    adpd: 3.0,
    mom: null,
    other: 2.7,
    raw_pl: null,
    raw_pn: null,
    turnout: null,
    abela: null,
    opp: null,
    opp_name: 'Grech',
    none: null,
    gov_perf: null,
    field: '1–6 Feb 2025',
    url: 'https://lovinmalta.com/news/labour-maintains-16000-vote-lead-over-pn-vincent-marmara-survey-shows/',
    notes: 'PL lead 5.9pp = ~15,800 votes. 22% undecided.',
    lead: 5.9,
    raw_lead: 3.7,
  },
  {
    date: '2025-02-13',
    pollster: 'MaltaToday',
    sample: 672,
    pl: 48.2,
    pn: 43.9,
    adpd: 3.4,
    mom: 4.0,
    other: 0.5,
    raw_pl: null,
    raw_pn: null,
    turnout: 75.5,
    abela: 46.8,
    opp: 24.7,
    opp_name: 'Grech',
    none: 28.5,
    gov_perf: 2.9,
    field: '29 Jan–13 Feb 2025',
    url: 'https://www.maltatoday.com.mt/news/data_and_surveys/133651/labour_back_in_pole_position_with_12000vote_lead_over_pn_as_momentum_breaks_new_ground',
    notes: 'First MT poll of 2025. PL lead = 11,613 votes (4.3pp). First poll measuring Momentum (4%).',
    lead: 4.3,
  },
  {
    date: '2025-02-19',
    pollster: 'Esprimi (Times of Malta)',
    sample: 600,
    pl: 51.5,
    pn: 45.0,
    adpd: null,
    mom: null,
    other: 3.5,
    raw_pl: null,
    raw_pn: null,
    turnout: 81.3,
    abela: null,
    opp: null,
    opp_name: 'Grech',
    none: null,
    gov_perf: 3.0,
    field: '30 Jan–12 Feb 2025',
    url: 'https://newsbook.com.mt/en/maltatoday-times-of-malta-polls-show-labour-leading-over-pn/',
    notes: 'PL lead 6.5pp = ~18,700 votes.',
    lead: 6.5,
  },
  {
    date: '2025-04-13',
    pollster: 'MaltaToday',
    sample: 651,
    pl: 51.6,
    pn: 42.8,
    adpd: 1.5,
    mom: 2.2,
    other: 1.9,
    raw_pl: null,
    raw_pn: null,
    turnout: 77.5,
    abela: 46.5,
    opp: 20.0,
    opp_name: 'Grech',
    none: 33.5,
    gov_perf: 3.1,
    field: '28 Mar–8 Apr 2025',
    url: 'https://www.independent.com.mt/articles/2025-04-13/local-news/PL-leads-PN-by-24-000-in-latest-survey-6736269347',
    notes: 'PL lead = 24,145 votes (8.8pp). PL absolute majority for first time since EP election.',
    lead: 8.8,
  },
  {
    date: '2025-06-06',
    pollster: 'MaltaToday',
    sample: 647,
    pl: 53.3,
    pn: 39.7,
    adpd: 1.6,
    mom: 3.1,
    other: 2.3,
    raw_pl: null,
    raw_pn: null,
    turnout: 80.3,
    abela: 50.0,
    opp: 18.8,
    opp_name: 'Grech',
    none: 31.2,
    gov_perf: 3.1,
    field: '28 May–6 Jun 2025',
    url: 'https://www.maltatoday.com.mt/news/data_and_surveys/135330/pn_in_freefall_as_labour_leads_by_14_points_',
    notes: "PL lead = 38,802 votes (13.6pp). Grech's worst rating since Feb 2024.",
    lead: 13.6,
  },
  {
    date: '2025-10-02',
    pollster: 'MaltaToday',
    sample: 741,
    pl: 48.8,
    pn: 45.9,
    adpd: 2.4,
    mom: 2.0,
    other: 0.9,
    raw_pl: null,
    raw_pn: null,
    turnout: 81.6,
    abela: 42.5,
    opp: 33.9,
    opp_name: 'Borg',
    none: 23.6,
    gov_perf: null,
    field: '24 Sep–2 Oct 2025',
    url: 'https://www.independent.com.mt/articles/2025-10-05/local-news/Labour-holds-slim-lead-over-PN-in-first-survey-after-Borg-s-election-6736273688',
    notes: "First MT poll after Borg's leadership win. PL lead = 8,394 votes. Within MoE ±3.6pp.",
    lead: 2.9,
  },
  {
    date: '2025-10-04',
    pollster: 'Sagalytics',
    sample: 1200,
    pl: 51.7,
    pn: 43.2,
    adpd: 3.5,
    mom: null,
    other: 1.6,
    raw_pl: null,
    raw_pn: null,
    turnout: 83.0,
    abela: null,
    opp: null,
    opp_name: 'Borg',
    none: null,
    gov_perf: null,
    field: '25 Sep–1 Oct 2025',
    url: 'https://lovinmalta.com/news/exclusive-labour-bounces-back-with-25000-vote-lead-new-marmara-survey-shows/',
    notes: "Exclusive to Lovin Malta. First Sagalytics poll after Borg's win. ~25,000 vote lead.",
    lead: 8.5,
    raw_lead: 6.9,
  },
  {
    date: '2025-10-12',
    pollster: 'Esprimi (Times of Malta)',
    sample: 600,
    pl: 50.1,
    pn: 45.1,
    adpd: null,
    mom: null,
    other: 4.8,
    raw_pl: null,
    raw_pn: null,
    turnout: 83.7,
    abela: 50.1,
    opp: 48.7,
    opp_name: 'Borg',
    none: null,
    gov_perf: 3.1,
    field: '29 Sep–9 Oct 2025',
    url: 'https://www.independent.com.mt/articles/2025-10-12/local-news/Labour-s-lead-over-PN-shrinks-to-5-new-survey-shows-6736273896',
    notes: 'PL lead 5pp = ~15,000 votes. Conducted just after Borg became PN leader (10 Sep).',
    lead: 5.0,
  },
  {
    date: '2025-11-16',
    pollster: 'MaltaToday',
    sample: 799,
    pl: 48.7,
    pn: 46.1,
    adpd: 4.1,
    mom: 1.2,
    other: 0.0,
    raw_pl: null,
    raw_pn: null,
    turnout: 81.0,
    abela: 45.2,
    opp: 35.3,
    opp_name: 'Borg',
    none: null,
    gov_perf: null,
    field: '5–13 Nov 2025',
    url: 'https://www.independent.com.mt/articles/2025-11-16/local-news/Labour-marginally-loses-ground-in-spite-of-best-budget-ever-survey-6736284839',
    notes: 'Post-budget poll. PL lead = 7,600 votes. Within MoE ±3.5pp. Borg honeymoon visible.',
    lead: 2.6,
  },
  {
    date: '2025-11-22',
    pollster: 'Sagalytics',
    sample: 1200,
    pl: 52.3,
    pn: 43.5,
    adpd: 1.7,
    mom: null,
    other: 2.5,
    raw_pl: null,
    raw_pn: null,
    turnout: null,
    abela: 43.6,
    opp: 34.9,
    opp_name: 'Borg',
    none: 5.1,
    gov_perf: null,
    field: '13–20 Nov 2025',
    url: 'https://www.independent.com.mt/articles/2025-11-22/local-news/Labour-maintains-strong-lead-in-latest-Vince-Marmara-survey-6736284996',
    notes: '~26,000 vote lead. 12.7% undecided. Last public Marmarà poll for 3 months.',
    lead: 8.8,
    raw_lead: 7.5,
  },
  {
    date: '2026-01-16',
    pollster: 'MaltaToday',
    sample: 813,
    pl: 48.9,
    pn: 45.7,
    adpd: 2.3,
    mom: 2.3,
    other: 0.8,
    raw_pl: null,
    raw_pn: null,
    turnout: null,
    abela: null,
    opp: null,
    opp_name: 'Borg',
    none: null,
    gov_perf: null,
    field: '5–16 Jan 2026',
    url: 'https://en.wikipedia.org/wiki/Opinion_polling_for_the_2026_Maltese_general_election',
    notes: 'PL lead = 9,300 votes (3.2pp). Within MoE.',
    lead: 3.2,
  },
  {
    date: '2026-02-19',
    pollster: 'Sagalytics',
    sample: 1200,
    pl: 52.8,
    pn: 42.6,
    adpd: null,
    mom: null,
    other: 4.6,
    raw_pl: null,
    raw_pn: null,
    turnout: 77.0,
    abela: 44.4,
    opp: 34.7,
    opp_name: 'Borg',
    none: 9.2,
    gov_perf: null,
    field: '13–19 Feb 2026',
    url: 'https://lovinmalta.com/malta/marmara-survey-shows-labour-still-leading-as-gap-narrows/',
    notes: 'Lead = 25,000–29,000 votes (10.2pp). First Sagalytics poll since Nov 2025.',
    lead: 10.2,
    raw_lead: 8.6,
  },
  {
    date: '2026-03-08',
    pollster: 'MaltaToday',
    sample: 784,
    pl: 48.2,
    pn: 45.6,
    adpd: 3.7,
    mom: 2.1,
    other: 0.4,
    raw_pl: null,
    raw_pn: null,
    turnout: 80.7,
    abela: 44.1,
    opp: 37.7,
    opp_name: 'Borg',
    none: 18.2,
    gov_perf: 3.0,
    field: '~late Feb–early Mar 2026',
    url: 'https://www.maltatoday.com.mt/news/data_and_surveys/140177/labour_leads_by_7500_votes',
    notes: 'PL lead = 7,500 votes. Margin of error ±3.5pp. Gap within MoE.',
    lead: 2.6,
  },
  {
    date: '2026-04-16',
    pollster: 'Esprimi (Times of Malta)',
    sample: 600,
    pl: 51.3,
    pn: 45.0,
    adpd: null,
    mom: null,
    other: 3.7,
    raw_pl: null,
    raw_pn: null,
    turnout: null,
    abela: null,
    opp: null,
    opp_name: 'Borg',
    none: null,
    gov_perf: null,
    field: 'early–mid Apr 2026',
    url: 'https://newsbook.com.mt/en/alex-borg-calls-for-fixed-term-parliaments-to-end-election-date-uncertainty/',
    notes:
      'PL lead = 19,600 votes (6.3pp), widened by ~4,600 votes vs October 2025. Final ToM poll before 27 Apr snap-election announcement.',
    lead: 6.3,
  },
  {
    date: '2026-05-02',
    pollster: 'Sagalytics',
    sample: 1200,
    pl: 53.2,
    pn: 43.1,
    adpd: null,
    mom: null,
    other: 3.7,
    raw_pl: null,
    raw_pn: null,
    turnout: null,
    abela: null,
    opp: null,
    opp_name: 'Borg',
    none: 12.6,
    gov_perf: null,
    field: '23–29 Apr 2026',
    url: 'https://vincentmarmara.com/',
    notes:
      'First Sagalytics poll of the 2026 campaign, fielded across the 27 April snap-election announcement. Modelled lead 10.1pp (raw 11.0pp before MIT redistribution of 12.6% undecided). Scenario range 9–11pp, ~26,000–30,000 vote PL lead at 80% assumed turnout.',
    lead: 10.1,
    raw_lead: 11.0,
  },
];

// ---- POLLSTERS ------------------------------------------------------
// Methodology copy verbatim from the inspection HTML's three method cards.

export const POLLSTERS: Record<PollsterName, Pollster> = {
  MaltaToday: {
    color: '#222222',
    abbr: 'MT',
    abbr_bg: '#555555',
    fullname: 'MaltaToday in-house',
    // Lead-analyst names removed at editor's request — methodology
    // card now shows just "MaltaToday in-house". Empty string is
    // skipped by MethodologyCard's render so the line doesn't leave
    // a blank gap.
    lead_analyst: '',
    frequency: 'Monthly (skips July–August)',
    sample_typical: '650–800 respondents',
    moe: '±3.5–3.8 pp',
    method: 'Telephone interviews; demographic weighting; turnout modelling included',
    house_effect:
      'Closest to actual EP 2024 result. Tends to show smaller PL leads than Sagalytics.',
    notes:
      'Longest continuous tracker in Malta — running since the early 2000s. Publishes voting intentions, trust barometer, and government performance score.',
  },
  Sagalytics: {
    color: '#CF1B2B',
    abbr: 'SG',
    abbr_bg: '#CF1B2B',
    fullname: 'Sagalytics (Vincent Marmarà)',
    lead_analyst: 'Vincent Marmarà',
    frequency: 'Roughly monthly (gaps in unfavourable periods)',
    sample_typical: '1,200 respondents',
    moe: '±2.8 pp',
    method:
      'Telephone + mobile interviews; Multiple Imputation Technique applied to undecided/refused voters',
    house_effect:
      'Consistent 5–8pp larger PL lead than MaltaToday. 2024 EP forecasts overstated PL by ~10pp vs actual result.',
    notes:
      "Marmarà is openly the PL's pollster. Holds multiple government direct orders. Publishes via Facebook + It-Torċa (GWU-owned weekly). Sample includes 16+ year olds, calibrated against 2022 GE.",
  },
  'Esprimi (Times of Malta)': {
    color: '#1a4d9e',
    abbr: 'TM',
    abbr_bg: '#1a4d9e',
    fullname: 'Esprimi for Times of Malta',
    lead_analyst: 'Esprimi (commissioned by ToM)',
    frequency: 'Quarterly target, often less frequent',
    sample_typical: '500–600 respondents',
    moe: '±4.0 pp',
    method: 'Mixed methodology — online panel + telephone interviews',
    house_effect:
      "Typically between MT and Sagalytics readings. Tends to group third parties as 'Others'.",
    notes: 'ESOMAR member. Esprimi also runs quarterly omnibus surveys for commercial clients.',
  },
  '2024 EP Election (actual result)': {
    color: '#b8860b',
    abbr: 'VOTE',
    abbr_bg: '#b8860b',
    fullname: '2024 European Parliament Election (actual result)',
    lead_analyst: 'Electoral Commission of Malta',
    frequency: 'Once — 8 June 2024',
    sample_typical: 'Full electorate (turnout 73.0%)',
    moe: 'n/a — actual ballot count',
    method: 'Single transferable vote, first-count percentages',
    house_effect: 'n/a — this is the calibration anchor for the other pollsters.',
    notes:
      'Baseline reference: actual result of the 8 June 2024 European Parliament election in Malta. PL won by 8,454 first-count votes.',
  },
};

// ---- MILESTONES -----------------------------------------------------
// Campaign markers shown as vertical guide-lines on the trajectory chart.
// Dates match the SVG positions in the inspection HTML.

export const MILESTONES: Milestone[] = [
  { date: '2024-06-08', label: 'EP' },
  { date: '2024-10-28', label: "Budget '25" },
  { date: '2025-06-10', label: 'Grech resigns' },
  { date: '2025-09-06', label: 'Borg elected' },
  { date: '2025-10-27', label: "Budget '26" },
  { date: '2026-04-27', label: 'Snap called' },
];

// ---- HOUSE_EFFECT_ADJ -----------------------------------------------
// Calibration adjustment in pp, subtracted from each pollster's reported
// PL lead before averaging in `houseEffectAdjusted`. Anchored on the
// 2024 EP result: pollsters that over-predicted PL relative to that
// vote get a positive (subtracted) adjustment.

export const HOUSE_EFFECT_ADJ: Record<PollsterName, number> = {
  MaltaToday: 0,
  Sagalytics: 7.5,
  'Esprimi (Times of Malta)': 7.1,
  '2024 EP Election (actual result)': 0,
};

// ---- ANCHOR ---------------------------------------------------------
// Denormalised reference to the EP 2024 result — the calibration point
// for the house-effect adjustment.

export const ANCHOR = {
  date: '2024-06-08',
  pl: 45.3,
  pn: 42.0,
  lead: 3.3,
  label: 'EP Election 2024',
} as const;
